import { useAuth } from '@clerk/react'
import { useCallback } from 'react'

// In dev, Vite proxies /api → localhost:8787. In prod, if VITE_API_BASE_URL is set
// we prepend it (used for *.workers.dev deploys before DNS is configured). Once
// images.frankenvibes.dev has a Worker route for /api/*, leave VITE_API_BASE_URL
// unset so requests stay same-origin.
const API_BASE = import.meta.env.VITE_API_BASE_URL ?? ''

export function useAuthenticatedFetch() {
  const { getToken } = useAuth()

  return useCallback(
    async (url: string, options: RequestInit = {}) => {
      const token = await getToken()
      const fullUrl = url.startsWith('http') ? url : `${API_BASE}${url}`
      const res = await fetch(fullUrl, {
        ...options,
        headers: {
          ...options.headers,
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })
      if (!res.ok) {
        const error = await res.json().catch(() => ({ error: res.statusText }))
        throw new Error(error.error ?? `Request failed: ${res.status}`)
      }
      return res
    },
    [getToken],
  )
}

export interface GenerateRequest {
  model: string
  prompt: string
  negative_prompt?: string
  type: 'image' | 'video'
  width?: number
  height?: number
  steps?: number
  seed?: number
  lora_repo?: string
}

export interface Generation {
  id: string
  user_id: string
  model: string
  prompt: string
  negative_prompt?: string
  type: 'image' | 'video'
  width?: number
  height?: number
  steps?: number
  seed?: number
  r2_key?: string
  r2_url?: string
  thumbnail_r2_key?: string
  metadata?: string
  nsfw: boolean
  status: 'pending' | 'generating' | 'complete' | 'failed'
  created_at: string
}

export interface ApiResponse<T> {
  data?: T
  error?: string
}

// ─────────────────────────────────────────────────────────────────────────────
// Conversation Mode

export interface Conversation {
  id: string
  user_id: string
  title: string
  model: string
  created_at: string
  updated_at: string
}

export interface ConversationTurn {
  id: string
  conversation_id: string
  role: 'user' | 'assistant'
  content: string
  refined_prompt: string | null
  generation_id: string | null
  // JSON-encoded array of R2 public URLs for user-uploaded reference images.
  // Server stores as TEXT; consumers should JSON.parse before rendering.
  image_urls?: string | null
  r2_url?: string | null
  created_at: string
}

export interface UploadedImage {
  key: string
  url: string
  content_type: string
}

// Read a File as base64 (without the data: URL prefix). Used by ChatPanel
// to package reference images for POST /api/uploads.
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = reader.result as string
      const comma = result.indexOf(',')
      resolve(comma >= 0 ? result.slice(comma + 1) : result)
    }
    reader.onerror = () => reject(reader.error ?? new Error('Read failed'))
    reader.readAsDataURL(file)
  })
}

export interface ConversationWithTurns {
  conversation: Conversation
  turns: ConversationTurn[]
}

export interface SendMessageResponse {
  assistantTurn: ConversationTurn
  usage?: {
    input_tokens: number
    output_tokens: number
    cache_read_input_tokens?: number
  }
}
